import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { Toast } from 'primereact/toast'
import { Tag } from 'primereact/tag'
import { GenerateXMLResponse, OptimizePlanResponse, ProductionQuantity_Input, ProductionQuantity_Output } from '@/api'
import { useAtom, useAtomValue } from 'jotai'
import { optimizePlanResponseAtom, excelPlanResponseAtom } from '@/atoms/optimization'
import { NumberWithBold } from '../types'
import { normalizeOptimizePlanResponse, NormalizedOptimizePlanResponse } from '@/utils/normalizeOptimizeResponse'

interface XMLExportDialogProps {
  visible: boolean
  onHide: () => void
  xmlResponse: GenerateXMLResponse | null
  onUserChangesApplied?: (userChanges: Record<string, Record<number, NumberWithBold>>) => void
}

interface FileWatchEvent {
  file_id: string
  event_type: 'uploaded' | 'modified' | 'deleted' | 'initial' | 'waiting' | 'error'
  timestamp: string
  file_size?: number
  message: string
}

interface XMLReadResponse {
  dates: string[]
  productions: Record<string, ProductionQuantity_Input[]>
  parsed_date: string
  file_count: number
}

const XMLExportDialog: React.FC<XMLExportDialogProps> = ({
  visible,
  onHide,
  xmlResponse,
  onUserChangesApplied
}) => {
  const toastRef = useRef<Toast>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  // Jotai atoms
  const [optimizePlanResponse, setOptimizePlanResponse] = useAtom(optimizePlanResponseAtom)
  const excelPlanResponse = useAtomValue(excelPlanResponseAtom)

  // ファイル監視の状態
  const [isWatching, setIsWatching] = useState(false)
  const [lastUploadTime, setLastUploadTime] = useState<string | null>(null)
  const [watchEvents, setWatchEvents] = useState<FileWatchEvent[]>([])
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected')
  const [isImporting, setIsImporting] = useState(false)

  // フルURLを生成する関数
  const getFullUrl = (relativePath: string) => {
    const { protocol, host } = window.location
    return `${protocol}//${host}${relativePath}`
  }

  // file_idを取得する関数
  const getFileId = (xmlUrl: string) => {
    const matches = xmlUrl.match(/\/api\/xml\/download\/(.+)$/)
    return matches && matches[1] ? matches[1] : null
  }

  // Exchange URLを生成する関数
  const getExchangeUrl = (xmlUrl: string) => {
    const fileId = getFileId(xmlUrl)
    if (fileId) {
      const { protocol, host } = window.location
      return `${protocol}//${host}/api/xml/exchange/${fileId}`
    }
    return ''
  }

  // SSE接続を開始する関数
  const startWatching = useCallback(() => {
    if (!xmlResponse?.xml_url) return

    const fileId = getFileId(xmlResponse.xml_url)
    if (!fileId) return

    stopWatching() // 既存の接続があれば停止

    setIsWatching(true)
    setConnectionStatus('connecting')
    setWatchEvents([])

    const eventSource = new EventSource(`/api/xml/watch/${fileId}`)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setConnectionStatus('connected')
      console.log('SSE connection opened')
    }

    eventSource.addEventListener('file-watch', (event: MessageEvent) => {
      try {
        const data: FileWatchEvent = JSON.parse(event.data)
        setWatchEvents(prev => [...prev, data])

        // アップロードまたは更新イベントの場合、取り込み日時を更新
        if (data.event_type === 'uploaded' || data.event_type === 'modified') {
          setLastUploadTime(data.timestamp)
          toastRef.current?.show({
            severity: 'success',
            summary: 'ファイル更新',
            detail: data.message,
            life: 3000
          })
        } else if (data.event_type === 'initial' && data.timestamp) {
          // 初期状態でファイルが存在する場合、取込日時を設定
          setLastUploadTime(data.timestamp)
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error)
      }
    })

    eventSource.addEventListener('error', (event: MessageEvent) => {
      try {
        const data: FileWatchEvent = JSON.parse(event.data)
        setWatchEvents(prev => [...prev, data])
        toastRef.current?.show({
          severity: 'error',
          summary: '監視エラー',
          detail: data.message,
          life: 5000
        })
      } catch (error) {
        console.error('Error parsing SSE error data:', error)
      }
    })

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error)
      setConnectionStatus('disconnected')
      toastRef.current?.show({
        severity: 'error',
        summary: '接続エラー',
        detail: 'ファイル監視接続でエラーが発生しました',
        life: 5000
      })
    }
  }, [xmlResponse?.xml_url])

  // SSE接続を停止する関数
  const stopWatching = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setIsWatching(false)
    setConnectionStatus('disconnected')
  }

  // ダイアログが表示されたときに監視を開始
  useEffect(() => {
    if (visible && xmlResponse?.xml_url) {
      // 少し遅延してから監視を開始（UIの初期化を待つため）
      const timer = setTimeout(() => {
        startWatching()
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [visible, xmlResponse?.xml_url, startWatching])

  // ダイアログが閉じられた時またはコンポーネントがアンマウントされた時にSSE接続を停止
  useEffect(() => {
    return () => {
      stopWatching()
    }
  }, [])

  // ダイアログが閉じられた時にSSE接続を停止
  const handleHide = () => {
    stopWatching()
    onHide()
  }

  // Exchange URLをクリップボードにコピーする機能
  const handleCopyExchangeUrl = async () => {
    if (!xmlResponse?.xml_url) return

    try {
      const exchangeUrl = getExchangeUrl(xmlResponse.xml_url)
      await navigator.clipboard.writeText(exchangeUrl)
      toastRef.current?.show({
        severity: 'success',
        summary: '成功',
        detail: 'Exchange URLをクリップボードにコピーしました',
        life: 2000
      })
    } catch (error) {
      console.error('クリップボードコピーエラー:', error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'URLのコピーに失敗しました',
        life: 3000
      })
    }
  }

  // ダウンロードURLをクリップボードにコピーする機能
  const handleCopyDownloadUrl = async () => {
    if (!xmlResponse?.xml_url) return

    try {
      const fullUrl = getFullUrl(xmlResponse.xml_url)
      await navigator.clipboard.writeText(fullUrl)
      toastRef.current?.show({
        severity: 'success',
        summary: '成功',
        detail: 'ダウンロードURLをクリップボードにコピーしました',
        life: 2000
      })
    } catch (error) {
      console.error('クリップボードコピーエラー:', error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'URLのコピーに失敗しました',
        life: 3000
      })
    }
  }

  // XMLファイルを直接ダウンロードする機能
  const handleDownloadXml = () => {
    if (!xmlResponse?.xml_url) return

    const link = document.createElement('a')
    link.href = getFullUrl(xmlResponse.xml_url)
    link.download = `production_plan_${xmlResponse.generated_date}.xml`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toastRef.current?.show({
      severity: 'success',
      summary: '成功',
      detail: 'ダウンロードを開始しました',
      life: 2000
    })
  }

  // XMLReadResponseからOptimizePlanResponseに変換する関数
  const convertXmlResponseToOptimizePlanResponse = (xmlData: XMLReadResponse): {
    response: NormalizedOptimizePlanResponse | null,
    userChanges: Record<string, Record<number, NumberWithBold>>
  } => {
    if (!optimizePlanResponse || !excelPlanResponse) {
      console.error('Current optimize plan response or excel plan response is not available')
      return { response: null, userChanges: {} }
    }

    try {
      // 日付の整合性をチェック
      const originalDates = optimizePlanResponse.dates
      const allXmlDates = xmlData.dates

      console.log('Original dates:', originalDates)
      console.log('All XML dates:', allXmlDates)

      // 元の日付範囲（開始日と終了日）を取得
      const startDate = originalDates[0]
      const endDate = originalDates[originalDates.length - 1]

      console.log('Date range to import:', startDate, '～', endDate)

      // XMLデータから指定した日付範囲のみを抽出
      const startIndex = allXmlDates.indexOf(startDate)
      const endIndex = allXmlDates.indexOf(endDate)

      if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
        throw new Error(`指定された日付範囲がXMLデータに存在しません。開始日: ${startDate}, 終了日: ${endDate}`)
      }

      // 指定範囲の日付とデータを抽出
      const filteredDates = allXmlDates.slice(startIndex, endIndex + 1)
      const filteredProductions: Record<string, ProductionQuantity_Output[]> = {}

      // UserChangesを抽出 (is_bold === trueのデータ)
      const userChanges: Record<string, Record<number, NumberWithBold>> = {}

      Object.keys(xmlData.productions).forEach(itemId => {
        const productionList = xmlData.productions[itemId].slice(startIndex, endIndex + 1) as ProductionQuantity_Output[]
        filteredProductions[itemId] = productionList

        // is_boldがtrueの生産数をUserChangesとして抽出
        productionList.forEach((production, dateIndex) => {
          if (production.is_bold) {
            if (!userChanges[itemId]) {
              userChanges[itemId] = {}
            }
            userChanges[itemId][dateIndex] = {
              value: production.value,
              is_bold: true
            }
          }
        })
      })

      console.log('Filtered dates:', filteredDates)
      console.log('Filtered date count:', filteredDates.length, 'vs original:', originalDates.length)

      // 新しい日付範囲に合わせて既存データを調整
      const updatedResponse: NormalizedOptimizePlanResponse = {
        ...optimizePlanResponse,
        dates: filteredDates,
        productions: filteredProductions,
      }

      // 日付範囲が変わった場合、他のデータも調整が必要
      if (filteredDates.length !== originalDates.length) {
        // capacitiesとprocessTimesを新しい日付範囲に合わせて調整
        const newLength = filteredDates.length
        const originalLength = originalDates.length

        if (newLength > originalLength) {
          // 日付が増えた場合：最後の値を繰り返し
          const lastCapacity = optimizePlanResponse.capacities[originalLength - 1] || { value: 8.0 } // 8時間をデフォルトに
          const lastProcessTime = optimizePlanResponse.processTimes[originalLength - 1] || { value: 0.0 }

          updatedResponse.capacities = [
            ...optimizePlanResponse.capacities,
            ...Array(newLength - originalLength).fill(lastCapacity)
          ]
          updatedResponse.processTimes = [
            ...optimizePlanResponse.processTimes,
            ...Array(newLength - originalLength).fill(lastProcessTime)
          ]
        } else {
          // 日付が減った場合：データを切り取り
          updatedResponse.capacities = optimizePlanResponse.capacities.slice(0, newLength)
          updatedResponse.processTimes = optimizePlanResponse.processTimes.slice(0, newLength)
        }
      } else {
        // 日付範囲が同じ場合はそのまま使用
        updatedResponse.capacities = optimizePlanResponse.capacities
        updatedResponse.processTimes = optimizePlanResponse.processTimes
      }

      // inventoriesとshipmentsも新しい日付範囲に合わせて調整
      // ここでは簡単な実装として、既存のデータを日付範囲に合わせて調整
      const newInventories: Record<string, Array<{ value: number }>> = {}
      const newShipments: Record<string, Array<{ value: number }>> = {}

      Object.keys(filteredProductions).forEach(itemId => {
        const originalInventory = optimizePlanResponse.inventories[itemId] || []
        const originalShipment = optimizePlanResponse.shipments[itemId] || []

        if (filteredDates.length !== originalDates.length) {
          if (filteredDates.length > originalDates.length) {
            // 日付が増えた場合：最後の値を繰り返し
            const lastInventory = originalInventory[originalInventory.length - 1] || { value: 0 }
            const lastShipment = originalShipment[originalShipment.length - 1] || { value: 0 }

            newInventories[itemId] = [
              ...originalInventory,
              ...Array(filteredDates.length - originalDates.length).fill(lastInventory)
            ]
            newShipments[itemId] = [
              ...originalShipment,
              ...Array(filteredDates.length - originalDates.length).fill(lastShipment)
            ]
          } else {
            // 日付が減った場合：データを切り取り
            newInventories[itemId] = originalInventory.slice(0, filteredDates.length)
            newShipments[itemId] = originalShipment.slice(0, filteredDates.length)
          }
        } else {
          newInventories[itemId] = originalInventory
          newShipments[itemId] = originalShipment
        }
      })

      updatedResponse.inventories = newInventories
      updatedResponse.shipments = newShipments

      return { response: updatedResponse, userChanges }
    } catch (error) {
      console.error('Error converting XML data to OptimizePlanResponse:', error)
      return { response: null, userChanges: {} }
    }
  }

  // 取り込み処理を実行する機能
  const handleImportXml = async () => {
    if (!xmlResponse?.xml_url) return

    setIsImporting(true)
    try {
      const response = await fetch('/api/xml/read', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const xmlData: XMLReadResponse = await response.json()

      console.log('XML取り込みデータ:', {
        dates: xmlData.dates,
        productionKeys: Object.keys(xmlData.productions),
        dateCount: xmlData.dates.length,
        itemCount: Object.keys(xmlData.productions).length
      })

      // XMLデータをOptimizePlanResponseに変換
      const { response: convertedData, userChanges } = convertXmlResponseToOptimizePlanResponse(xmlData)

      if (convertedData) {
        // 在庫シミュレーションを実行
        console.log('在庫シミュレーションを開始...')
        const optimizeRequest = {
          dates: convertedData.dates,
          capacities: convertedData.capacities,
          productions: convertedData.productions,
          optimize: "simulation_only" as const
        }

        const simulationResponse = await fetch('/api/production-plan/optimize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(optimizeRequest)
        })

        if (!simulationResponse.ok) {
          throw new Error(`在庫シミュレーションエラー: ${simulationResponse.status}`)
        }

        const simulatedData: OptimizePlanResponse = await simulationResponse.json()
        console.log('在庫シミュレーション結果:', simulatedData)

        // optimizePlanResponseAtomを更新
        setOptimizePlanResponse(normalizeOptimizePlanResponse(simulatedData))

        // UserChangesがある場合は親コンポーネントに通知
        if (Object.keys(userChanges).length > 0 && onUserChangesApplied) {
          console.log('UserChanges detected:', userChanges)
          onUserChangesApplied(userChanges)
        }

        toastRef.current?.show({
          severity: 'success',
          summary: '取り込み完了',
          detail: `XMLファイルを取り込み、在庫シミュレーションが完了しました。品目数: ${Object.keys(xmlData.productions).length}、日付: ${xmlData.dates.length}日分（${xmlData.dates[0]}〜${xmlData.dates[xmlData.dates.length - 1]}）${Object.keys(userChanges).length > 0 ? `\nユーザー変更: ${Object.keys(userChanges).length}品目` : ''}`,
          life: 5000
        })

        console.log('Imported XML data:', xmlData)
        console.log('Updated optimize plan response:', simulatedData)
      } else {
        throw new Error('XMLデータの変換に失敗しました')
      }

    } catch (error) {
      console.error('XML取り込みエラー:', error)
      toastRef.current?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'XMLファイルの取り込みに失敗しました',
        life: 3000
      })
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <>
      <Dialog
        header="XML入出力設定完了"
        visible={visible}
        style={{ width: '600px' }}
        onHide={onHide}
      >
        {xmlResponse && (
          <div className="flex flex-column gap-1">
            <Message
              severity="success"
              text="生産計画XMLファイルが正常に生成されました。以下のExchange URLでVBAからデータの入出力が可能です。"
            />

            <div className="field">
              <label htmlFor="exchange-url" className="font-bold">Exchange URL (VBA用):</label>
              <div className="flex gap-2 mt-1">
                <InputText
                  id="exchange-url"
                  value={getExchangeUrl(xmlResponse.xml_url)}
                  readOnly
                  className="flex-1"
                />
                <Button
                  icon="pi pi-copy"
                  outlined
                  onClick={handleCopyExchangeUrl}
                  tooltip="Exchange URLをコピー"
                />
              </div>
            </div>

            <div className="field">
              <label className="font-bold">生成日時:</label>
              <p className="mt-0">{xmlResponse.generated_date}</p>
            </div>

            <div className="field">
              <label className="font-bold">取込日時:</label>
              <div className="flex align-items-center gap-2 mt-1">
                <p className="m-0">{lastUploadTime ? new Date(lastUploadTime).toLocaleString('ja-JP') : '-'}</p>
                {isWatching && (
                  <Tag
                    value={
                      connectionStatus === 'connected' ? '監視中' :
                      connectionStatus === 'connecting' ? '接続中' : '切断'
                    }
                    severity={
                      connectionStatus === 'connected' ? 'success' :
                      connectionStatus === 'connecting' ? 'warning' : 'danger'
                    }
                    icon={
                      connectionStatus === 'connected' ? 'pi pi-check-circle' :
                      connectionStatus === 'connecting' ? 'pi pi-spinner pi-spin' : 'pi pi-times-circle'
                    }
                  />
                )}
              </div>
            </div>

            <div className="field">
              <label className="font-bold">有効期限:</label>
              <p className="mt-0">{new Date(xmlResponse.expires_at).toLocaleString('ja-JP')}</p>
            </div>

            <Message
              severity="info"
              text="Exchange URLを使用してVBAからデータの取得(GET)と送信(POST)が可能です。"
            />

            <div className="flex justify-content-end gap-2">
              {lastUploadTime && (
                <Button
                  label="取り込み"
                  icon="pi pi-download"
                  onClick={handleImportXml}
                  loading={isImporting}
                  disabled={isImporting}
                />
              )}
              <Button
                label="閉じる"
                severity="secondary"
                onClick={handleHide}
              />
            </div>
          </div>
        )}
      </Dialog>

      <Toast ref={toastRef} />
    </>
  )
}

export default XMLExportDialog
